import {
    ChangeDetectionStrategy,
    Component,
    inject,
    ViewEncapsulation,
    input,
    output,
    signal,
    OnInit,
} from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { ItemService } from "../../util/items/item.service";
import { PredicateType, PredicateNode } from "./ingestion.predicate.builder.component";
import { ReportingFieldInfo, AnswerType, DataIngestionService } from "./data.ingestion.service";
import { MultiselectModelI } from "../s25-multiselect/s25.multiselect.component";
import { SearchCriteriaType } from "../../pojo/SearchCriteriaI";
import { NAME_RESOLVABLE_FIELD_TYPES, isNameResolvableFieldType } from "./data.ingestion.const";
import { S25Util } from "../../util/s25-util";

interface OperatorOption {
    value: string;
    label: string;
}

@TypeManagerDecorator("s25-ng-predicate-node")
@Component({
    selector: "s25-ng-predicate-node",
    template: `
        @if (isInit() && node()) {
            <div
                class="predicate-node"
                [class.field-node]="node()?.type === PredicateType.FIELD"
                [class.group-node]="node()?.type !== PredicateType.FIELD"
            >
                <!-- Field Node -->
                @if (node()?.type === PredicateType.FIELD) {
                    <div class="field-controls">
                        <select [(ngModel)]="node().columnName" (change)="onNodeChanged()">
                            <option value="">Select Field</option>
                            @for (field of availableFields(); track field.reportingFieldCode) {
                                <option [value]="field.reportingFieldCode">
                                    {{ field.reportingFieldCode }} ({{ field.answerType }})
                                </option>
                            }
                        </select>

                        <select [(ngModel)]="node().op" (change)="onNodeChanged()">
                            @for (operator of getAvailableOperators(); track operator.value) {
                                <option [value]="operator.value">
                                    {{ operator.label }}
                                </option>
                            }
                        </select>

                        @if (!isNullOperator(node().op)) {
                            @if (isBooleanField()) {
                                <select [(ngModel)]="node().value" (change)="onNodeChanged()" class="value-input">
                                    <option value="">Select...</option>
                                    <option value="true">True</option>
                                    <option value="false">False</option>
                                </select>
                            } @else if (isDurationField()) {
                                <s25-ng-duration-field
                                    [value]="$any(node().value)"
                                    (valueChange)="onDurationValueChange($event)"
                                />
                            } @else if (isSpecialFieldType()) {
                                <div class="special-field-selector">
                                    @if (showMultiselect()) {
                                        <s25-ng-multiselect-search-criteria
                                            [type]="getSearchCriteriaType()"
                                            [modelBean]="multiselectModel"
                                            (onDone)="onMultiselectDone($event)"
                                            [usePopover]="true"
                                            [popoverOnBody]="true"
                                            [popoverPlacement]="['auto', 'left', 'right', 'bottom']"
                                        ></s25-ng-multiselect-search-criteria>
                                    }
                                </div>
                            } @else {
                                <input
                                    [type]="getInputType()"
                                    [(ngModel)]="node().value"
                                    (input)="onNodeChanged()"
                                    [placeholder]="getValuePlaceholder()"
                                    class="value-input"
                                />
                            }
                        }

                        @if (!isRoot()) {
                            <button class="btn btn-sm btn-danger" (click)="removeNode()">×</button>
                        }
                    </div>
                }

                <!-- Group Node -->
                @if (node()?.type !== PredicateType.FIELD) {
                    <div class="group-controls">
                        <div class="group-header">
                            <select [(ngModel)]="node().op" (change)="onNodeChanged()">
                                <option value="And">AND</option>
                                <option value="Or">OR</option>
                            </select>
                            @if (!isRoot()) {
                                <button class="btn btn-sm btn-danger" (click)="removeNode()">×</button>
                            }
                        </div>

                        <div class="group-children">
                            @for (child of node().children; track $index; let i = $index) {
                                <s25-ng-predicate-node
                                    [node]="child"
                                    [availableFields]="availableFields()"
                                    [level]="level() + 1"
                                    [isRoot]="false"
                                    (nodeChanged)="onChildChanged($event)"
                                    (nodeRemoved)="onChildRemoved($event)"
                                >
                                </s25-ng-predicate-node>
                            }
                        </div>

                        <div class="group-actions">
                            <button class="btn btn-sm" (click)="addChildField()">Add Field</button>
                            <button class="btn btn-sm" (click)="addChildGroup()">Add Group</button>
                        </div>
                    </div>
                }
            </div>
        }
    `,
    styles: `
        .predicate-node {
            margin-bottom: 10px;
        }

        .field-node {
            padding: 10px;
            border: 1px solid var(--color--border);
            border-radius: 4px;
            background: var(--color--surface-2-bg);
        }

        .group-node {
            border: 1px solid var(--color--link-text);
            border-radius: 4px;
            background: var(--color--surface-2-bg);
        }

        .field-controls {
            display: flex;
            gap: 10px;
            align-items: center;
            flex-wrap: wrap;
            max-width: 100%;
        }

        .field-controls select,
        .field-controls input {
            padding: 5px 10px;
            border: 1px solid var(--color--border-bold);
            border-radius: 4px;
        }

        .value-input {
            min-width: 150px;
        }

        .special-field-selector {
            position: relative;
            max-width: 100%;
            overflow: visible;
            flex-shrink: 0;
        }

        .group-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 10px;
            background: var(--color--link-text);
            color: white;
            border-radius: 4px 4px 0 0;
        }

        .group-header select {
            padding: 5px 10px;
            border: none;
            border-radius: 4px;
            background: var(--color--surface-bg);
            color: var(--color--text-strong);
        }

        .group-children {
            padding: 15px;
        }

        .group-actions {
            padding: 10px;
            background: var(--color--surface-2-bg);
            border-radius: 0 0 4px 4px;
            display: flex;
            gap: 10px;
        }

        .btn {
            padding: 5px 10px;
            border: 1px solid var(--color--border);
            background: var(--color--surface-bg);
            color: var(--color--text);
            cursor: pointer;
            border-radius: 4px;
        }

        .btn-sm {
            font-size: 0.875em;
            padding: 3px 6px;
        }

        .btn-danger {
            background: var(--color--danger);
            color: white;
            border-color: var(--color--danger);
        }

        .btn:hover {
            background: var(--color--surface-2-bg);
        }
    `,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class IngestionPredicateNodeComponent implements OnInit {
    node = input<PredicateNode>();
    availableFields = input<ReportingFieldInfo[]>([]);
    level = input<number>(0);
    isRoot = input<boolean>(false);
    nodeChanged = output<PredicateNode>();
    nodeRemoved = output<string>();

    private ingestionService = inject(DataIngestionService);
    private itemService = inject(ItemService);

    // Multiselect model for special field types
    multiselectModel: MultiselectModelI = this.createFreshMultiselectModel();

    /**
     * Create a fresh multiselect model with no persisted state
     */
    private createFreshMultiselectModel(): MultiselectModelI {
        return {
            showResult: true,
            onChange: () => {},
            selectedItems: [],
        };
    }

    /**
     * Reset the multiselect model completely
     */
    private resetMultiselectModel() {
        this.multiselectModel = this.createFreshMultiselectModel();
    }

    // Init signal to control when UI is shown
    isInit = signal<boolean>(false);

    // Signal to control multiselect visibility - toggling this destroys/recreates the component
    showMultiselect = signal<boolean>(false);

    // Expose enum to template
    PredicateType = PredicateType;

    async ngOnInit() {
        // Initialize special field names if this is a special field type with values
        if (this.isSpecialFieldType() && this.node()?.value) {
            await this.loadSpecialFieldNames();
        }
        // Show multiselect if field is already selected and is a special field type
        if (this.node()?.columnName && this.isSpecialFieldType()) {
            this.showMultiselect.set(true);
        }
        this.isInit.set(true);
    }

    /**
     * Recreate multiselect component by hiding and showing it
     */
    private recreateMultiselect() {
        if (this.isSpecialFieldType()) {
            // Hide to destroy, then show to recreate in next tick
            this.showMultiselect.set(false);
            setTimeout(() => this.showMultiselect.set(true), 0);
        } else {
            this.showMultiselect.set(false);
        }
    }

    // Track the previous field code to detect changes
    private previousFieldCode: string = "";

    onNodeChanged() {
        const currentFieldCode = this.node()?.columnName || "";
        const fieldChanged = this.previousFieldCode !== currentFieldCode;

        if (this.node()?.columnName) {
            const availableOperators = this.getAvailableOperators();
            const currentOperator = this.node()!.op;

            // If current operator is not available for this field type, reset to first available
            if (!availableOperators.some((op) => op.value === currentOperator)) {
                const newNode = { ...this.node()! };
                newNode.op = (availableOperators[0]?.value || "Equals") as PredicateNode["op"];
                newNode.value = "" as PredicateNode["value"];

                if (this.isSpecialFieldType()) {
                    this.resetMultiselectModel();
                    this.recreateMultiselect();
                }

                this.previousFieldCode = currentFieldCode;
                this.nodeChanged.emit(newNode);
                return;
            }
        }

        const isSpecialField = this.isSpecialFieldType();

        if (isSpecialField) {
            if (fieldChanged) {
                this.resetMultiselectModel();
                this.recreateMultiselect();
                if (this.node()?.value) {
                    // Wait for multiselect recreation (0ms) to complete before loading names
                    setTimeout(() => this.loadSpecialFieldNames(), 10);
                }
            } else if (!this.showMultiselect()) {
                this.showMultiselect.set(true);
            }
        } else if (this.showMultiselect()) {
            this.showMultiselect.set(false);
        }

        this.previousFieldCode = currentFieldCode;
        this.nodeChanged.emit(this.node()!);
    }

    onChildChanged(child: PredicateNode) {
        this.nodeChanged.emit(this.node()!);
    }

    onChildRemoved(childId: string) {
        if (this.node()!.children) {
            const newNode = { ...this.node()! };
            newNode.children = newNode.children!.filter((child) => child.id !== childId);
            this.nodeChanged.emit(newNode);
        }
    }

    removeNode() {
        this.nodeRemoved.emit(this.node()!.id!);
    }

    addChildField() {
        const newNode = { ...this.node()! };
        if (!newNode.children) {
            newNode.children = [];
        }

        const newField: PredicateNode = {
            id: this.generateId(),
            type: PredicateType.FIELD,
            op: "Equals" as const,
            columnName: this.availableFields().length > 0 ? this.availableFields()[0].reportingFieldCode : "",
            value: "",
            valueType: this.availableFields().length > 0 ? this.availableFields()[0].answerType : "STRING",
        };

        newNode.children.push(newField);
        this.nodeChanged.emit(newNode);
    }

    addChildGroup() {
        const newNode = { ...this.node()! };
        if (!newNode.children) {
            newNode.children = [];
        }

        const newGroup: PredicateNode = {
            id: this.generateId(),
            op: "And" as const,
            children: [
                {
                    id: this.generateId(),
                    type: PredicateType.FIELD,
                    op: "Equals" as const,
                    columnName: this.availableFields().length > 0 ? this.availableFields()[0].reportingFieldCode : "",
                    value: "",
                    valueType: this.availableFields().length > 0 ? this.availableFields()[0].answerType : "STRING",
                },
            ],
        };

        newNode.children.push(newGroup);
        this.nodeChanged.emit(newNode);
    }

    isNullOperator(operator: string): boolean {
        return operator === "Equals" && this.node()?.value === null;
    }

    getValuePlaceholder(): string {
        if (!this.node()?.columnName) {
            return "Select field first";
        }

        const field = this.getCurrentField();
        if (!field?.answerType) {
            return "Enter value";
        }

        switch (field.answerType) {
            case "INTEGER":
                return "Enter number";
            case "FLOAT":
                return "Enter decimal number";
            case "DATETIME":
                return "Enter date/time";
            case "BOOLEAN":
                return "true or false";
            default:
                return "Enter text";
        }
    }

    getInputType(): string {
        const field = this.getCurrentField();
        if (!field?.answerType) {
            return "text";
        }

        switch (field.answerType) {
            case "INTEGER":
                return "number";
            case "FLOAT":
                return "number";
            case "DATETIME":
                return "datetime-local";
            case "BOOLEAN":
                return "text"; // Boolean uses dropdown, not input
            default:
                return "text";
        }
    }

    isBooleanField(): boolean {
        return this.getCurrentField()?.answerType === "BOOLEAN";
    }

    isDurationField(): boolean {
        return this.getCurrentField()?.fieldType === "DURATION";
    }

    onDurationValueChange(value: string) {
        this.node()!.value = value;
        this.onNodeChanged();
    }

    getAvailableOperators(): OperatorOption[] {
        if (!this.node()?.columnName) {
            return [];
        }

        const field = this.getCurrentField();
        if (!field) {
            return [];
        }

        // Special field types (EVENT_TYPE, EVENT, ORGANIZATION, CONTACT, ROLE, ORG_TYPE, ORG_RATING, CATEGORIES, ORG_CATEGORIES) only support In/NotIn operators
        if (isNameResolvableFieldType(field.fieldType)) {
            return [
                { value: "In", label: "IN" },
                { value: "NotIn", label: "NOT IN" },
            ];
        }

        const allOperators: OperatorOption[] = [
            { value: "Equals", label: "=" },
            { value: "NotEquals", label: "!=" },
            { value: "GreaterThan", label: ">" },
            { value: "GreaterThanOrEqual", label: ">=" },
            { value: "LessThan", label: "<" },
            { value: "LessThanOrEqual", label: "<=" },
            { value: "In", label: "IN" },
            { value: "NotIn", label: "NOT IN" },
            { value: "Like", label: "Contains" },
            { value: "NotLike", label: "Not Contains" },
        ];

        switch (field.answerType) {
            case AnswerType.INTEGER:
            case AnswerType.FLOAT:
                // Numeric fields: comparison operators
                return allOperators.filter((op) =>
                    [
                        "Equals",
                        "NotEquals",
                        "GreaterThan",
                        "GreaterThanOrEqual",
                        "LessThan",
                        "LessThanOrEqual",
                    ].includes(op.value),
                );

            case AnswerType.DATETIME:
                // Date fields: comparison operators
                return allOperators.filter((op) =>
                    [
                        "Equals",
                        "NotEquals",
                        "GreaterThan",
                        "GreaterThanOrEqual",
                        "LessThan",
                        "LessThanOrEqual",
                    ].includes(op.value),
                );

            case AnswerType.BOOLEAN:
                // Boolean fields: only equality
                return allOperators.filter((op) => ["Equals", "NotEquals"].includes(op.value));

            case AnswerType.STRING:
            case AnswerType.LARGE_STRING:
                // String fields: all operators except numeric comparisons
                return allOperators.filter(
                    (op) => !["LessThan", "LessThanOrEqual", "GreaterThan", "GreaterThanOrEqual"].includes(op.value),
                );

            case AnswerType.FILE:
                // File fields: only equality
                return allOperators.filter((op) => ["Equals", "NotEquals"].includes(op.value));

            default:
                return allOperators;
        }
    }

    isSpecialFieldType(): boolean {
        return isNameResolvableFieldType(this.getCurrentFieldType());
    }

    getCurrentField(): ReportingFieldInfo | undefined {
        if (!this.node()?.columnName) return undefined;
        return this.availableFields().find((f) => f.reportingFieldCode === this.node()!.columnName);
    }

    getCurrentFieldType(): string | undefined {
        return this.getCurrentField()?.fieldType;
    }

    getSearchCriteriaType(): SearchCriteriaType["type"] {
        const fieldType = this.getCurrentFieldType();
        if (!isNameResolvableFieldType(fieldType)) {
            throw new Error(`Invalid field type: ${fieldType}`);
        }
        return NAME_RESOLVABLE_FIELD_TYPES[fieldType].searchCriteriaType;
    }

    async loadSpecialFieldNames(): Promise<void> {
        if (!this.node()?.value) return;

        const values = S25Util.array.forceArray(this.node()!.value);
        const fieldType = this.getCurrentFieldType();
        if (!isNameResolvableFieldType(fieldType)) return;

        const config = NAME_RESOLVABLE_FIELD_TYPES[fieldType];

        try {
            this.multiselectModel.selectedItems = (
                await this.itemService.criteria(config.searchCriteriaType).fetchAll(values.map(Number))
            ).map((item) => (item.itemTypeId = <any>config.itemTypeId));
        } catch (error) {
            console.error("Error loading special field names:", error);
            this.multiselectModel.selectedItems = values.map((value: any) => ({
                itemId: Number(value),
                itemName: `${fieldType} ${value}`,
                itemTypeId: config.itemTypeId,
            }));
        }
    }

    onMultiselectDone(selectedItems: any[]) {
        // Convert multiselect items back to node value format
        const values = selectedItems.map((item) => item.itemId);
        this.node()!.value = values;
        this.onNodeChanged();
    }

    private generateId(): string {
        return "node_" + Math.random().toString(36).substr(2, 9);
    }
}
